import React, { useState } from 'react';
import axios from 'axios';
import { BACKEND_URL } from '../lib/backendUrl';
import { Opportunity, OpportunitySubmission } from '../types';
import { useAuth } from '../contexts/AuthContext';
import './SubmitOpportunityModal.css';

const API_URL = BACKEND_URL;

interface SubmitOpportunityModalProps {
  onClose: () => void;
  onSuccess: (opportunity: Opportunity) => void;
}

const SubmitOpportunityModal: React.FC<SubmitOpportunityModalProps> = ({ onClose, onSuccess }) => {
  const { user, token } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [formData, setFormData] = useState<OpportunitySubmission>({
    title: '',
    description: '',
    type: 'internship',
    category: '',
    company: '',
    stipend: '',
    deadline: '',
    link: '',
    tags: [],
  });

  const [tagInput, setTagInput] = useState('');

  const opportunityTypes: Array<'internship' | 'challenge' | 'hackathon' | 'workshop' | 'competition'> = [
    'internship',
    'challenge',
    'hackathon',
    'workshop',
    'competition',
  ];

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleAddTag = () => {
    if (tagInput.trim() && formData.tags) {
      setFormData((prev) => ({
        ...prev,
        tags: [...(prev.tags || []), tagInput.trim()],
      }));
      setTagInput('');
    }
  };

  const handleRemoveTag = (index: number) => {
    setFormData((prev) => ({
      ...prev,
      tags: prev.tags?.filter((_, i) => i !== index) || [],
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      if (!formData.title.trim() || !formData.description.trim()) {
        throw new Error('Title and description are required');
      }

      const submissionData = {
        ...formData,
        postedBy: 'anonymous',
        status: 'pending' as const,
      };

      const response = await axios.post(
        `${API_URL}/api/opportunities`,
        submissionData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );

      setSuccess(true);
      // Don't add pending opportunities to the front-end list
      // They will appear in admin notifications instead
      // Once approved, they'll show up in the Career Hub

      setTimeout(() => {
        onClose();
      }, 2000);
    } catch (error: any) {
      console.error('Failed to submit opportunity:', error);
      
      let errorMessage = 'Failed to submit opportunity. Please try again.';
      
      if (error.response?.status === 401) {
        errorMessage = 'You must be logged in to post an opportunity. Please sign in first.';
      } else if (error.response?.status === 403) {
        errorMessage = 'You do not have permission to post opportunities.';
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.response?.data?.error) {
        errorMessage = error.response.data.error;
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="submit-opportunity-modal" onClick={(e) => e.stopPropagation()}>
        {/* Modal Header */}
        <div className="modal-header">
          <h2 className="modal-title">_INITIALIZE_NEW_NODE</h2>
          <button className="modal-close" onClick={onClose} aria-label="Close modal">
            ×
          </button>
        </div>

        {/* Success State */}
        {success && (
          <div className="success-state">
            <div className="success-icon">✓</div>
            <h3>SUBMISSION_ACCEPTED</h3>
            <p>Your opportunity has been queued for admin review</p>
            <p className="success-hint">Status: PENDING_APPROVAL</p>
          </div>
        )}

        {/* Form Content */}
        {!success && (
          <form onSubmit={handleSubmit} className="opportunity-form">
            <div className="form-section">
              <h3 className="section-title">01. CORE_DATA</h3>
              
              {/* Title Field */}
              <div className="form-group">
                <label htmlFor="title" className="form-label">
                  Title <span className="required">*</span>
                </label>
                <input
                  type="text"
                  id="title"
                  name="title"
                  value={formData.title}
                  onChange={handleInputChange}
                  placeholder="e.g., Summer Internship 2024"
                  className="form-input"
                  maxLength={100}
                  disabled={loading}
                />
                <span className="char-count">{formData.title.length}/100</span>
              </div>

              {/* Type & Category Row */}
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="type" className="form-label">
                    Type <span className="required">*</span>
                  </label>
                  <select
                    id="type"
                    name="type"
                    value={formData.type}
                    onChange={handleInputChange}
                    className="form-select"
                    disabled={loading}
                  >
                    {opportunityTypes.map((type) => (
                      <option key={type} value={type}>
                        {type.charAt(0).toUpperCase() + type.slice(1)}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label htmlFor="category" className="form-label">
                    Category
                  </label>
                  <input
                    type="text"
                    id="category"
                    name="category"
                    value={formData.category}
                    onChange={handleInputChange}
                    placeholder="e.g., Web Development, AI/ML"
                    className="form-input"
                    disabled={loading}
                  />
                </div>
              </div>

              {/* Description */}
              <div className="form-group">
                <label htmlFor="description" className="form-label">
                  Description <span className="required">*</span>
                </label>
                <textarea
                  id="description"
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  placeholder="Describe the opportunity in detail..."
                  className="form-textarea"
                  rows={4}
                  maxLength={500}
                  disabled={loading}
                />
                <span className="char-count">{formData.description.length}/500</span>
              </div>
            </div>

            {/* Details Section */}
            <div className="form-section">
              <h3 className="section-title">02. SYSTEM_DETAILS</h3>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="company" className="form-label">
                    Company / Organization
                  </label>
                  <input
                    type="text"
                    id="company"
                    name="company"
                    value={formData.company}
                    onChange={handleInputChange}
                    placeholder="e.g., Google, Microsoft"
                    className="form-input"
                    disabled={loading}
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="stipend" className="form-label">
                    Stipend / Prize
                  </label>
                  <input
                    type="text"
                    id="stipend"
                    name="stipend"
                    value={formData.stipend}
                    onChange={handleInputChange}
                    placeholder="e.g., ₹50,000/month"
                    className="form-input"
                    disabled={loading}
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="deadline" className="form-label">
                    Application Deadline
                  </label>
                  <input
                    type="date"
                    id="deadline"
                    name="deadline"
                    value={formData.deadline}
                    onChange={handleInputChange}
                    className="form-input"
                    disabled={loading}
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="link" className="form-label">
                    Application Link
                  </label>
                  <input
                    type="url"
                    id="link"
                    name="link"
                    value={formData.link}
                    onChange={handleInputChange}
                    placeholder="https://..."
                    className="form-input"
                    disabled={loading}
                  />
                </div>
              </div>
            </div>

            {/* Tags Section */}
            <div className="form-section">
              <h3 className="section-title">Tags</h3>
              <div className="tag-input-group">
                <input
                  type="text"
                  value={tagInput}
                  onChange={(e) => setTagInput(e.target.value)}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleAddTag();
                    }
                  }}
                  placeholder="e.g., Python, Web Dev (Press Enter to add)"
                  className="form-input"
                  disabled={loading}
                />
                <button
                  type="button"
                  onClick={handleAddTag}
                  className="tag-add-btn"
                  disabled={loading || !tagInput.trim()}
                >
                  ADD
                </button>
              </div>

              {formData.tags && formData.tags.length > 0 && (
                <div className="tags-display">
                  {formData.tags.map((tag, index) => (
                    <span key={index} className="display-tag">
                      {tag}
                      <button
                        type="button"
                        onClick={() => handleRemoveTag(index)}
                        className="tag-remove"
                        disabled={loading}
                      >
                        ×
                      </button>
                    </span>
                  ))}
                </div>
              )}
            </div>

            {/* Error Message */}
            {error && (
              <div className="error-message">
                <span className="error-icon">⚠</span>
                {error}
              </div>
            )}

            {/* Form Actions */}
            <div className="form-actions">
              <button
                type="button"
                onClick={onClose}
                className="btn btn-secondary"
                disabled={loading}
              >
                CANCEL
              </button>
              <button
                type="submit"
                className="btn btn-primary"
                disabled={loading}
              >
                {loading ? 'SUBMITTING...' : 'SUBMIT_TO_ADMIN'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default SubmitOpportunityModal;
